import React, { memo, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { Alert, Checkbox } from 'antd';

import fontService, { FontWeight, TextType } from 'services/FontService';
import { mobile } from 'services/ui';
import { Colors } from 'context';
import { Button } from 'atoms';
import {
  ICareerQuizQuestion,
  ICareerQuizResult,
  getCareerErrorText,
  useSubmitCareerQuiz,
} from 'core/hooks/CareerCenter';

export interface ICareerLessonQuiz {
  lessonId: string;
  questions: ICareerQuizQuestion[];
}

type OptionState = 'neutral' | 'correct' | 'wrong';

/**
 * Career-lesson self-check quiz (spec 0020 Фаза C).
 *
 * Every option is a checkbox (multi-select) — single-vs-multi is NOT exposed by
 * the backend, which scores by EXACT-SET match, so rendering all as checkboxes
 * is the honest UI. «Проверить» submits `[{ questionId, selectedIndexes }]`;
 * the result reveals `correctIndexes` (hidden until submit) so we can show the
 * score, per-question ✓/✗, and highlight the right options. «Пройти заново»
 * clears the attempt for a fresh run.
 */
export const CareerLessonQuiz = memo(({ lessonId, questions }: ICareerLessonQuiz) => {
  const { t } = useTranslation();
  const { submitQuiz, loading } = useSubmitCareerQuiz();

  const [selections, setSelections] = useState<Record<string, number[]>>({});
  const [result, setResult] = useState<ICareerQuizResult | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isGraded = result !== null;

  const toggleOption = useCallback(
    (questionId: string, index: number, checked: boolean) => {
      if (isGraded) {
        return;
      }
      setSelections((prev) => {
        const current = new Set(prev[questionId] ?? []);
        if (checked) {
          current.add(index);
        } else {
          current.delete(index);
        }
        return { ...prev, [questionId]: Array.from(current).sort((a, b) => a - b) };
      });
    },
    [isGraded],
  );

  const onSubmit = useCallback(async () => {
    setSubmitError(null);
    try {
      const answers = questions.map((question) => ({
        questionId: question.id,
        selectedIndexes: selections[question.id] ?? [],
      }));

      const graded = await submitQuiz(lessonId, answers);

      if (!graded || !graded.success) {
        // По КОДУ, а не по тексту сервера: сообщение сервера русское.
        setSubmitError(getCareerErrorText(t, graded?.errorCode));
        return;
      }

      setResult(graded);
    } catch {
      setSubmitError(`${t('careerCenter.lesson.quiz.submitError')}`);
    }
  }, [questions, selections, submitQuiz, lessonId, t]);

  const onRetry = useCallback(() => {
    setResult(null);
    setSubmitError(null);
    setSelections({});
  }, []);

  const optionState = useCallback(
    (questionId: string, index: number): OptionState => {
      if (!result) {
        return 'neutral';
      }
      const questionResult = result.byQuestion[questionId];
      const isCorrect = questionResult?.correctIndexes.includes(index) ?? false;
      const isSelected = (selections[questionId] ?? []).includes(index);

      if (isCorrect) {
        return 'correct';
      }
      if (isSelected) {
        return 'wrong';
      }
      return 'neutral';
    },
    [result, selections],
  );

  const scoreLabel = useMemo(
    () =>
      result
        ? t('careerCenter.lesson.quiz.score', { score: result.score, max: result.maxScore })
        : '',
    [result, t],
  );

  if (!questions.length) {
    return null;
  }

  return (
    <QuizBlock>
      <QuizHead>
        <QuizTitle>{t('careerCenter.lesson.quiz.title')}</QuizTitle>
        <QuizHint>{t('careerCenter.lesson.quiz.hint')}</QuizHint>
      </QuizHead>

      {questions.map((question, questionIndex) => {
        const questionResult = result?.byQuestion[question.id];
        return (
          <Question key={question.id}>
            <QuestionHead>
              <QuestionText>
                {questionIndex + 1}. {question.text}
              </QuestionText>
              {isGraded ? (
                <QuestionMark data-correct={questionResult?.correct ? 'yes' : 'no'} aria-hidden>
                  {questionResult?.correct ? '✓' : '✗'}
                </QuestionMark>
              ) : null}
            </QuestionHead>

            <Options>
              {question.options.map((option, optionIndex) => {
                const state = optionState(question.id, optionIndex);
                const checked = (selections[question.id] ?? []).includes(optionIndex);
                return (
                  <Option key={optionIndex} data-state={state}>
                    <Checkbox
                      checked={checked}
                      disabled={isGraded}
                      onChange={(event) =>
                        toggleOption(question.id, optionIndex, event.target.checked)
                      }>
                      {option}
                    </Checkbox>
                    {state === 'correct' ? (
                      <OptionTag data-state="correct">
                        {t('careerCenter.lesson.quiz.correct')}
                      </OptionTag>
                    ) : state === 'wrong' ? (
                      <OptionTag data-state="wrong">
                        {t('careerCenter.lesson.quiz.incorrect')}
                      </OptionTag>
                    ) : null}
                  </Option>
                );
              })}
            </Options>
          </Question>
        );
      })}

      {submitError ? <Alert type="error" showIcon message={submitError} /> : null}

      {isGraded ? (
        <ResultRow>
          <ScoreBadge>{scoreLabel}</ScoreBadge>
          <RetryButton type="button" onClick={onRetry}>
            {t('careerCenter.lesson.quiz.retry')}
          </RetryButton>
        </ResultRow>
      ) : (
        <SubmitButton type="button" onClick={onSubmit} isLoading={loading} disabled={loading}>
          {t('careerCenter.lesson.quiz.submit')}
        </SubmitButton>
      )}
    </QuizBlock>
  );
});

const QuizBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid ${Colors.system_gray};
`;

const QuizHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const QuizTitle = styled.h3`
  margin: 0;
  ${fontService.text({ size: 18, lineHeight: 133, weight: FontWeight.bold })}
`;

const QuizHint = styled.p`
  margin: 0;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
  color: ${Colors.gray_dark};
`;

const Question = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-sizing: border-box;
  width: 100%;
  padding: 20px;
  border: 1px solid ${Colors.system_gray};
  border-radius: 12px;
  background-color: ${Colors.white};
`;

const QuestionHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const QuestionText = styled.div`
  ${fontService.text({ type: TextType.normal, weight: FontWeight.bold })}
`;

const QuestionMark = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: ${Colors.white};
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })}

  &[data-correct='yes'] {
    background-color: ${Colors.marker_green};
  }

  &[data-correct='no'] {
    background-color: ${Colors.signal_red};
  }
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Option = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border: 1px solid ${Colors.system_gray};
  border-radius: 8px;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}

  &[data-state='correct'] {
    border-color: ${Colors.marker_green};
  }

  &[data-state='wrong'] {
    border-color: ${Colors.signal_red};
  }
`;

const OptionTag = styled.span`
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 10px;
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.medium })}

  &[data-state='correct'] {
    color: ${Colors.marker_green};
  }

  &[data-state='wrong'] {
    color: ${Colors.signal_red};
  }
`;

const ResultRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const ScoreBadge = styled.div`
  padding: 8px 16px;
  border-radius: 10px;
  background-color: ${Colors.bg_gray};
  ${fontService.text({ type: TextType.normal, weight: FontWeight.bold })}
`;

const RetryButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: ${Colors.main};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}

  &:hover {
    color: ${Colors.main_dark};
  }
`;

const SubmitButton = styled(Button)`
  width: auto;
  align-self: flex-start;

  ${mobile} {
    width: 100%;
  }
`;
